import axios, { AxiosError, AxiosInstance } from 'axios';

export const API_HOST = process.env.REACT_APP_API_HOST || 'https://ippbx.picksco.com';
export const BASIC_API_HOST = process.env.REACT_APP_BASIC_API_HOST || 'https://callis-basic-api.picksco.com';
export const CORE_API_HOST = process.env.REACT_APP_CORE_API_HOST || 'https://callis-core-api.picksco.com';

export const AUTH_STORAGE_KEYS = {
  activeToken: 'accessToken',
  activeUser: 'user',
  basicToken: 'basicAccessToken',
  coreToken: 'coreAccessToken',
  basicUser: 'basicUser',
  coreUser: 'coreUser',
  mode: 'callis.auth.mode',
} as const;

export type ApiTarget = 'basic' | 'core';

const BASIC_OPERATIONS = new Set([
  'GET /call/log',
  'GET /contact/list',
  'GET /contact/getFieldStructure',
  'POST /contact',
  'GET /call/ai/result',
  'POST /ai-analyze',
  'GET /user',
  'POST /user/register',
  'GET /user/roles',
  'PUT /user/password',
  'GET /user/api-keys',
  'POST /user/api-keys',
  'GET /teams',
  'POST /teams',
  'GET /dashboard/total-calls',
  'GET /dashboard/success-rate',
  'GET /dashboard/inbound-calls',
  'GET /dashboard/outbound-calls',
  'GET /dashboard/missed-calls',
  'GET /dashboard/average-call-time',
  'GET /dashboard/distribution',
  'GET /dashboard/agent-status',
  'GET /calendar/tags',
]);

const getPathname = (url?: string) => {
  if (!url) return '';
  try {
    return new URL(url, 'http://localhost').pathname;
  } catch {
    return url.split(/[?#]/, 1)[0];
  }
};

const isBasicOperation = (method = '', url?: string) => {
  const normalizedMethod = method.toUpperCase();
  const pathname = getPathname(url);

  if (BASIC_OPERATIONS.has(`${normalizedMethod} ${pathname}`)) return true;
  if (normalizedMethod === 'GET' && /^\/call\/log\/\d+$/.test(pathname)) return true;
  if (['GET', 'PUT', 'DELETE'].includes(normalizedMethod) && /^\/contact\/\d+$/.test(pathname)) return true;
  if (['GET', 'PUT', 'DELETE'].includes(normalizedMethod) && /^\/user\/\d+$/.test(pathname)) return true;
  if (normalizedMethod === 'PUT' && /^\/user\/api-keys\/\d+\/allowed-ip-ranges$/.test(pathname)) return true;
  if (normalizedMethod === 'POST' && /^\/user\/api-keys\/\d+\/revoke$/.test(pathname)) return true;
  return ['PUT', 'DELETE'].includes(normalizedMethod) && /^\/teams\/\d+$/.test(pathname);
};

export const resolveApiTarget = (mode: string | null | undefined, method = '', url?: string): ApiTarget => (
  mode === 'basic' && isBasicOperation(method, url) ? 'basic' : 'core'
);

const clearAuthStorage = () => {
  Object.values(AUTH_STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem('callis.auth.loginMode');
};

const handleResponseError = (error: AxiosError) => {
  if (error.response) {
    const status = error.response.status;

    if (status === 401) {
      clearAuthStorage();
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    } else if (status === 403) {
      const event = new CustomEvent('api-forbidden', { detail: { message: '권한이 없습니다.' } });
      window.dispatchEvent(event);
    }
  }
  return Promise.reject(error);
};

const createApi = (baseURL: string, tokenKey: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(tokenKey);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use((response) => response, handleResponseError);

  return client;
};

const createDispatchApi = (): AxiosInstance => {
  const client = axios.create({
    baseURL: CORE_API_HOST,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(
    (config) => {
      const target = resolveApiTarget(
        localStorage.getItem(AUTH_STORAGE_KEYS.mode),
        config.method,
        config.url
      );
      const tokenKey = target === 'basic' ? AUTH_STORAGE_KEYS.basicToken : AUTH_STORAGE_KEYS.coreToken;
      config.baseURL = target === 'basic' ? BASIC_API_HOST : CORE_API_HOST;
      const token = localStorage.getItem(tokenKey);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use((response) => response, handleResponseError);

  return client;
};

export const basicApi = createApi(BASIC_API_HOST, AUTH_STORAGE_KEYS.basicToken);
export const coreApi = createApi(CORE_API_HOST, AUTH_STORAGE_KEYS.coreToken);

export default createDispatchApi();
